import { defineComponent as _defineComponent } from 'vue'
import { PrivateRate } from "@/api/types/offers";
import BaseDateTimePicker from '@/components-UI/BaseDateTimePicker.vue';
import BaseInput from '@/components-UI/BaseInput.vue';
import BaseTable from '@/components-UI/table/BaseTable.vue';
import DateCell from '@/components-UI/table/cells/DateCell.vue';
import NumberCell from '@/components-UI/table/cells/NumberCell.vue';
import store from '@/store';
import { usePrivateRatesStore } from '@/store_v3/PrivateRatesStore';
import { ITableHeader } from '@/utils';
import { CellsType, FormattedItem, prepareItems, RawConfig } from '@/utils/CellsFactory';
import { getFormatDate } from '@/utils/formatDates';
import { addMinutes, isMatch, isValid, parse, subMinutes } from 'date-fns';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

type Field = 'full_price' | 'price_after_vat' | 'income' | 'revenue' | 'start_date' | 'end_date';
interface OfferGoal {
    offer_id?: number;
    goal_id: number;
    goal_name?: string;
    full_price: number | string | null;
    price_after_vat: number | string | null;
    income: number | string | null;
    revenue: number | string | null;
    revenue_type?: string;
    start_date?: string | null;
    end_date?: string | null;
    legacy_id?: number;
    [k: string]: any;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CreatePrivateRateGoals',
  setup(__props) {

const {
    createForm,
    changedGoals,
    editableActiveRates,
    isIncludeDepartment,
    isOfferTrafficAPI,
} = storeToRefs(usePrivateRatesStore());
const selectedGoals = ref<number[]>([]);

const headers = ref<ITableHeader<keyof PrivateRate>[]>([
    { text: 'ID цели', value: 'goal_id', align: 'center' },
    { text: 'Цель', value: 'goal_name' },
    { section: 'Текущая цена', text: 'Входящая цена', value: 'now_full_price', align: 'right' },
    { section: 'Текущая цена', text: 'Входящая цена без НДС', value: 'now_price_after_vat', align: 'right', lineBreak: true, width: '105px' },
    { section: 'Текущая цена', text: 'Доход для немарк. трафика', value: 'now_income', align: 'right', lineBreak: true, width: '115px' },
    { section: 'Текущая цена', text: 'Доход для марк. трафика', value: 'now_revenue', align: 'right', lineBreak: true, width: '115px' },
    { section: 'Текущая цена', text: 'Дата начала', value: 'now_start_date' },
    { section: 'Текущая цена', text: 'Дата окончания', value: 'now_end_date', width: '170px' },
    { section: 'Новая цена', text: 'Входящая цена', value: 'full_price', width: '112px', align: 'right' },
    { section: 'Новая цена', text: 'Входящая цена без НДС', value: 'price_after_vat', align: 'right', lineBreak: true, width: '105px' },
    { section: 'Новая цена', text: 'Доход для немарк. трафика', value: 'income', align: 'right', lineBreak: true, width: '115px' },
    { section: 'Новая цена', text: 'Доход для марк. трафика', value: 'revenue', align: 'right', lineBreak: true, width: '115px' },
    { section: 'Новая цена', text: 'Дата начала', value: 'start_date', width: '170px' },
    { section: 'Новая цена', text: 'Дата окончания (необязательно)', value: 'end_date', width: '170px' },
]);

const currencyGetter = store.getters['catalogueModule/GET_CURRENT_CURRENCY_ICON'];

const extractOfferGoal = (formatted: FormattedItem<any>) =>
    (Object.values(formatted)[0].componentData.item as OfferGoal);

const getColumns = computed(() => headers.value.map(h => h.value));

const config = computed<RawConfig<any>>(() => ({
    [CellsType.Route]: {
        goal_id: {
            name: 'offer-goal',
            isChip: true,
            valueKey: 'goal_id',
            query: () => ({ offer_id: createForm.value.offerObj?.legacy_id }),
            text: 'legacy_id',
        },
        goal_name: {
            name: 'offer-goal',
            valueKey: 'goal_id',
            query: () => ({ offer_id: createForm.value.offerObj?.legacy_id }),
            text: 'goal_name',
        },
    },
    [CellsType.Uniq]: {
        full_price: {},
        price_after_vat: {},
        income: {},
        revenue: {},
        start_date: {},
        end_date: {},
        now_full_price: {},
        now_price_after_vat: {},
        now_income: {},
        now_revenue: {},
        now_start_date: {},
        now_end_date: {},
    },
}));

const getActiveRates = computed(() => {
    const arr = createForm.value.activeRates ?? [];
    const map = new Map<number, any>();
    for (const r of arr) {
        if (r?.goal_id == null) continue;
        if (!map.has(r.goal_id)) map.set(r.goal_id, r);
    }
    return map;
});

const hasActiveRate = (item: any) => getActiveRates.value.has(item.legacy_id);
const getActiveRateValue = (id: number, field: Field) => {
    const rate = getActiveRates.value.get(id);
    return rate ? (rate[field] ?? '') : '';
};

const rawItems = computed(() => {
    const offerGoals = createForm.value.offerObj?.goals ?? [];
    const todayStart = getFormatDate(new Date().setHours(0, 0, 0, 0), 'yyyy-MM-dd HH:mm');
    const rawItems: OfferGoal[] = offerGoals
        .filter(g => g?.active)
        .map(g => ({
            offer_id: createForm.value.offerObj?.legacy_id,
            goal_id: g.id,
            goal_name: g.name,
            income: null,
            revenue: null,
            revenue_type: g?.revenue?.type,
            start_date: todayStart,
            end_date: null,
            legacy_id: g?.legacy_id,
            now_full_price: null,
            now_price_after_vat: null,
            now_income: null,
            now_revenue: null,
            now_start_date: null,
            now_end_date: null,
        }));

    return rawItems;
});

const formatItems = computed(() => {
    const items = prepareItems(rawItems.value, config.value, getColumns.value);

    for (const fi of items) {
        const item = extractOfferGoal(fi);
        if (!getActiveRates.value.has(item.legacy_id)) {
            if (fi.now_full_price) fi.now_full_price.colspan = 6;
            if (fi.now_price_after_vat) fi.now_price_after_vat.hidden = true;
            if (fi.now_income) fi.now_income.hidden = true;
            if (fi.now_revenue) fi.now_revenue.hidden = true;
            if (fi.now_start_date) fi.now_start_date.hidden = true;
            if (fi.now_end_date) fi.now_end_date.hidden = true;
        }
    }

    return items;
});

const selectedGoalsSet = computed(() => new Set(selectedGoals.value));
const isSelected = (goalId?: number) => (goalId == null ? false : selectedGoalsSet.value.has(goalId));

const currencySymbol = (item: Partial<OfferGoal>) => {
    if (item.revenue_type === 'percent') return '%';
    return currencyGetter(createForm.value.offerObj?.currency);
};

const moneyMask = (item: Partial<OfferGoal>) => {
    return {
        mask: '0.99',
        tokens: {
            0: { pattern: '\\d', multiple: true },
            9: { pattern: '\\d', optional: true },
        },
        postProcess: (value: string) => {
            const [intPart = '', fracPart] = (value ?? '').split('.');
            const isPercent = currencySymbol(item) === '%';
            const isOneHundred = intPart === '100' && isPercent;
            const maxLength = isPercent ? (isOneHundred ? 3 : 2) : 9;
            let result = intPart.slice(0, maxLength);
            if (fracPart !== undefined && !isOneHundred) {
                result += '.' + fracPart.slice(0, 2);
            }
            return result;
        },
    };
};

const getDatePlusMinusMinute = (value: string, operation: 'plus' | 'minus') => {
    const format = isMatch(value, 'yyyy-MM-dd HH:mm') ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd HH:mm:ss';
    const parsed = parse(value, format, new Date());
    if (!isValid(parsed)) return '';
    const res = operation === 'plus' ? addMinutes(parsed, 1) : subMinutes(parsed, 1);
    return getFormatDate(res, 'yyyy-MM-dd HH:mm');
};

const updateOrAddObject = (newObject: OfferGoal) => {
    const idx = changedGoals.value.findIndex(o => o.goal_id === newObject.goal_id);
    if (idx !== -1) changedGoals.value.splice(idx, 1, { ...newObject });
    else changedGoals.value.push({ ...newObject });
};

const updateGoals = (item: OfferGoal, field: Field, value: string) => {
    item[field] = value;

    if (field === 'full_price') {
        calcPriceAfterVAT(item);
        calcIncomeAndRevenue(item);
    }

    if (field === 'price_after_vat') {
        calcIncomeAndRevenue(item);
    }

    if (field === 'start_date') {
        const rate = (createForm.value.activeRates ?? []).find(r => r.goal_id === item.legacy_id);
        if (rate) {
            const newEnd = getDatePlusMinusMinute(value, 'minus');
            rate.end_date = newEnd;
            editableActiveRates.value[item.legacy_id] = { id: rate.id, end_date: newEnd };
        }
    }

    updateOrAddObject(item);
};

const isVAT = computed(() => {
    const adv = createForm.value.advertiserObj;
    if (adv != null && typeof adv.vat === 'boolean') {
        return adv.vat;
    }
    return Boolean(createForm.value.offerObj?.advertiser?.vat);
});

const calcPriceAfterVAT = (item: OfferGoal) => {
    const { full_price: fp } = item;
    const num = typeof fp === 'number' ? fp : Number(fp);
    if (!Number.isFinite(num)) return;
    item.price_after_vat = isVAT.value ? Math.floor(Math.max(num / 1.22, 0)) : num;
};

const calcIncomeAndRevenue = (item: OfferGoal) => {
    const { price_after_vat: pav } = item;
    item.income = createForm.value.type === 'from_rate_always' ? Math.ceil(Math.max(pav / 1.03, 0)) : pav;
    item.revenue = createForm.value.type === 'above' || createForm.value.type === 'none' ? pav : Math.ceil(Math.max(pav / 1.03, 0));
};

const updateActiveRates = (item: OfferGoal, value: string) => {
    const rate = (createForm.value.activeRates ?? []).find(r => r.goal_id === item.legacy_id);
    if (!rate) return;
    editableActiveRates.value[item.legacy_id] = { id: rate.id, end_date: value };
    rate.end_date = value;
    item.start_date = getDatePlusMinusMinute(value, 'plus');
    updateOrAddObject(item);
};

const applyForAll = (field: Field, value: string, target: 'active' | 'new') => {
    for (const fi of formatItems.value) {
        const item = extractOfferGoal(fi);
        if (!isSelected(item.goal_id)) continue;
        if (target === 'new') updateGoals(item, field, value);
        else updateActiveRates(item, value);
    }
};

const updateSelectedGoals = (items: FormattedItem<any>[]) => {
    const selectedIds = items.map(i => i.goal_id.componentData.value) as number[];
    const changedItems = items.map(i => i.goal_id.componentData.item as OfferGoal);

    const oldSet = new Set(selectedGoals.value);
    const newSet = new Set(selectedIds);

    for (const id of newSet) {
        if (oldSet.has(id)) continue;

        const added = changedItems.find(ci => ci.goal_id === id);
        if (!added) continue;

        const rate = (createForm.value.activeRates ?? []).find(r => r.goal_id === added.legacy_id);
        if (!rate) continue;

        if (rate.end_date) {
            const startDate = getDatePlusMinusMinute(rate.end_date, 'plus');
            updateGoals(added, 'start_date', startDate);
        } else {
            const newEnd = getDatePlusMinusMinute(added.start_date ?? '', 'minus');
            rate.end_date = newEnd;
            editableActiveRates.value[added.legacy_id] = { id: rate.id, end_date: newEnd };
        }
    }

    selectedGoals.value = selectedIds;
    changedGoals.value = changedItems;
};

watch(() => createForm.value.type, () => {
    const changedIds = changedGoals.value.map(i => i.goal_id);
    for (const item of rawItems.value) {
        if (changedIds.includes(item.goal_id)) {
            calcIncomeAndRevenue(item);
        }
    }
});

watch(isVAT, () => {
    for (const item of changedGoals.value) {
        const fp = item.full_price;
        if (fp === null || fp === undefined || fp === '') continue;
        calcPriceAfterVAT(item);
        calcIncomeAndRevenue(item);
        updateOrAddObject(item);
    }
});

return { __sfc: true,createForm, changedGoals, editableActiveRates, isIncludeDepartment, isOfferTrafficAPI, selectedGoals, headers, currencyGetter, extractOfferGoal, getColumns, config, getActiveRates, hasActiveRate, getActiveRateValue, rawItems, formatItems, selectedGoalsSet, isSelected, currencySymbol, moneyMask, getDatePlusMinusMinute, updateOrAddObject, updateGoals, isVAT, calcPriceAfterVAT, calcIncomeAndRevenue, updateActiveRates, applyForAll, updateSelectedGoals, BaseDateTimePicker, BaseInput, BaseTable, DateCell, NumberCell }
}

})